package com.example.mystybag.appCore.logic.pages

import com.example.mystybag.appCore.model.pages.MenuUIState
import com.example.mystybag.appCore.model.pages.CompanyResponsiblePerson
import com.example.mystybag.common.logic.DialogNavigatorStandard
import com.example.mystybag.common.resources.SR
import com.example.mystybag.communication.Communication
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_JWT
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_ID
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_IS_STAFF
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_STAFF_LOCATION_IDS
import com.example.mystybag.core.expected.Preferences
import com.example.mystybag.core.logic.base.AppContext
import com.example.mystybag.core.logic.base.ChildViewModel
import com.example.mystybag.core.expected.getVersionName
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.database.communication.db.DatabaseProvider
import com.example.mystybag.communication.common.CommonCommands
import com.example.mystybag.communication.utils.onResponse
import com.example.mystybag.login.logic.NavigatorFeatureLoginAndRegistration
import com.example.mystybag.sharedmodels.project.requestModels.merchant.GetMerchantMenuDataRequestModel
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch

class MenuViewModel(context: AppContext): ChildViewModel<MenuUIState>(context, MenuUIState()) {

    override fun onCreate() {
        navControls.title = sharedString(SR.strings.menu) // "Account"
        updateState { it.copy(appVersion = getVersionName().ifEmpty { "1.0.0" }) }
        loadMenuData()
    }
    
    private fun loadMenuData() {
        val merchantId = Preferences.getLong(key = PREFERENCES_MERCHANT_ID, 0L)
        val isStaff = Preferences.getBoolean(key = PREFERENCES_IS_STAFF, false)
        if (merchantId == 0L) {
            updateState { it.copy(isLoading = false, isStaff = isStaff) }
            return
        }

        scope.launch {
            updateState { it.copy(isLoading = true, isStaff = isStaff) }
            
            commandBlock(showProgress = false, hideProgress = false) {
                CommonCommands.getMerchantMenuData(
                    GetMerchantMenuDataRequestModel(merchantId = merchantId)
                )
                    .onResponse(
                        success = { response ->
                            launchOnMain {
                                val responseModel = response as? com.example.mystybag.sharedmodels.project.responseModels.merchant.GetMerchantMenuDataResponseModel
                                val responsiblePersons = responseModel?.companyResponsiblePersons?.map { person ->
                                    CompanyResponsiblePerson(
                                        id = person.id ?: 0L,
                                        firstName = person.firstName ?: "",
                                        lastName = person.lastName ?: "",
                                        phoneNumber = person.phoneNumber ?: "",
                                        dialCode = person.dialCode ?: ""
                                    )
                                } ?: emptyList()
                                
                                updateState {
                                    it.copy(
                                        isLoading = false,
                                        isStaff = isStaff,
                                        iban = responseModel?.iban,
                                        ibanConfirmationDate = responseModel?.ibanConfirmationDate,
                                        companyName = responseModel?.companyName,
                                        companyResponsiblePersons = responsiblePersons
                                    )
                                }
                            }
                        },
                        baseError = { response ->
                            launchOnMain {
                                updateState { it.copy(isLoading = false) }
                            }
                        }
                    )
            }
        }
    }

    // Finance section actions
    fun navigateToStatements() {
        navigate(com.example.mystybag.appCore.logic.NavigatorStatements)
    }
    
    fun navigateToBankAccount() {
        // Navigate to bank account (IBAN) management page
        // This would need a NavigatorBankAccount or similar
    }
    
    // Business section actions
    fun navigateToMerchantProfile() {
        navigate(com.example.mystybag.appCore.logic.NavigatorMerchantProfileDetails)
    }
    
    fun navigateToContacts() {
        navigate(com.example.mystybag.appCore.logic.NavigatorContacts)
    }

    fun navigateToSettings() {
        navigate(com.example.mystybag.appCore.logic.NavigatorSettings)
    }
    
    fun navigateToCompanyResponsiblePersons() {
        navigate(com.example.mystybag.appCore.logic.NavigatorCompanyResponsiblePersonsList)
    }

    fun navigateToStaffList() {
        navigate(com.example.mystybag.appCore.logic.NavigatorStaffList)
    }

    fun onLogoutClicked() {
        scope.launch {
            showDialog(
                DialogNavigatorStandard(
                    title = sharedString(SR.strings.log_out),
                    message = sharedString(SR.strings.are_you_sure_you_want_to_proceed),
                    positiveTitle = sharedString(SR.strings.log_out_yes),
                    negativeTitle = sharedString(SR.strings.cancel),
                    stackedButtons = true
                )
            ).onPositiveClick {
                Communication.token = ""
                Preferences.clear()
                Preferences.setBoolean(PREFERENCES_IS_STAFF, false)
                Preferences.setString(PREFERENCES_STAFF_LOCATION_IDS, "")
                CoroutineScope(Dispatchers.Default).launch {
                    DatabaseProvider.clearDatabase()
                }
                
                // Navigate to WelcomeView
                launchOnMain {
                    replaceAll(NavigatorFeatureLoginAndRegistration())
                }
            }
        }
    }
}

